import { useState, useEffect, useRef } from 'react';
import { VIBE_EMOJIS } from '@/utils/vibes';
import { MapPin, Loader2, Bookmark, Share2, X, Copy, Check, Wand2, Pencil, Smartphone, Upload } from 'lucide-react';
import { useGeolocation } from '@/hooks/useGeolocation';
import { useLanguage } from '@/hooks/useLanguage';
import { useTheme } from '@/hooks/useTheme';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import { LanguageSelector } from './LanguageSelector';

interface InputScreenProps {
  onSubmit: (data: {
    location: string;
    timeAvailable: string;
    transport: string;
    vibes: string[];
    customVibe?: string;
    coords?: { lat: number; lng: number };
  }, includeClosedPlaces?: boolean) => void;
  errorMessage?: string | null;
  onDismissError?: () => void;
  previousData?: {
    location?: string;
    timeAvailable?: string;
    transport?: string;
    vibes?: string[];
    customVibe?: string;
    coords?: { lat: number; lng: number };
  };
  onOpenSettings?: () => void;
  onOpenSavedTrips?: () => void;
  filteredClosedCount?: number;
  isAuthenticated?: boolean;
  hasSavedTrips?: boolean;
  highlightMyTrips?: boolean;
  isPremium?: boolean;
  savedInterestsActive?: boolean;
  hasSavedInterests?: boolean;
  onToggleSavedInterests?: (value: boolean) => void;
  tasteDefaults?: { transport?: string; timeAvailable?: string };
  onOpenPro?: () => void;
  referralCode?: string | null;
}

export function InputScreen({
  onSubmit,
  errorMessage,
  onDismissError,
  previousData,
  onOpenSettings: _onOpenSettings,
  onOpenSavedTrips: _onOpenSavedTrips,
  filteredClosedCount = 0,
  isAuthenticated: _isAuthenticated,
  hasSavedTrips: _hasSavedTrips,
  highlightMyTrips: _highlightMyTrips,
  isPremium,
  savedInterestsActive,
  hasSavedInterests,
  onToggleSavedInterests,
  tasteDefaults,
  onOpenPro,
  referralCode
}: InputScreenProps) {
  const { t } = useLanguage();
  const { themeMode: _themeMode, setThemeMode: _setThemeMode } = useTheme();
  const { showBanner, dismissBanner, promptInstall, isInstallable, platform } = usePWAInstall();
  const [location, setLocation] = useState(previousData?.location || '');
  const [coords, setCoords] = useState<{ lat: number; lng: number } | undefined>(previousData?.coords);
  const [timeAvailable, setTimeAvailable] = useState(previousData?.timeAvailable || tasteDefaults?.timeAvailable || 'half-day');
  const [transport, setTransport] = useState(previousData?.transport || tasteDefaults?.transport || 'walking');
  const [vibes, setVibes] = useState<string[]>(previousData?.vibes || []);
  const [customVibe, setCustomVibe] = useState(previousData?.customVibe || '');
  const [customVibeActive, setCustomVibeActive] = useState(!!(previousData?.customVibe));
  const [editingCustomVibe, setEditingCustomVibe] = useState(false);
  const [customVibeDraft, setCustomVibeDraft] = useState('');
  const customVibeInputRef = useRef<HTMLInputElement>(null);
  const [errors, setErrors] = useState<{ location?: string; vibes?: string }>({});
  const [useCurrentLocation, setUseCurrentLocation] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const { getCurrentLocation, loading: geoLoading, error: geoError } = useGeolocation();
  const hasInitializedRef = useRef(false);
  const locationInputRef = useRef<HTMLInputElement>(null);

  const handleAutoLocation = async () => {
    const result = await getCurrentLocation();
    if (result) {
      setLocation(result.location);
      setCoords(result.coords || undefined);
      setUseCurrentLocation(true);
    }
  };

  const toggleUseCurrentLocation = async () => {
    if (!useCurrentLocation) {
      await handleAutoLocation();
    } else {
      setUseCurrentLocation(false);
    }
  };

  useEffect(() => {
    if (!hasInitializedRef.current && !location && useCurrentLocation) {
      hasInitializedRef.current = true;
      handleAutoLocation();
    }
  }, []);

  // Sync transport/time defaults when taste profile loads asynchronously
  const tasteAppliedRef = useRef(false);
  useEffect(() => {
    if (tasteAppliedRef.current || previousData?.transport) return;
    if (tasteDefaults?.transport) {
      tasteAppliedRef.current = true;
      setTransport(tasteDefaults.transport);
    }
  }, [tasteDefaults?.transport]);

  useEffect(() => {
    if (previousData?.timeAvailable) return;
    if (tasteDefaults?.timeAvailable) {
      setTimeAvailable(tasteDefaults.timeAvailable);
    }
  }, [tasteDefaults?.timeAvailable]);

  useEffect(() => {
    if (previousData?.location && previousData.location !== location) {
      setLocation(previousData.location);
    }
  }, [previousData?.location]);

  const handleVibeToggle = (vibe: string) => {
    setVibes(prev =>
      prev.includes(vibe)
        ? prev.filter(v => v !== vibe)
        : [...prev, vibe]
    );
    setErrors(prev => ({ ...prev, vibes: undefined }));
  };

  const handleCustomVibeEdit = () => {
    setCustomVibeDraft(customVibe);
    setEditingCustomVibe(true);
    setTimeout(() => customVibeInputRef.current?.focus(), 50);
  };

  const handleCustomVibeConfirm = () => {
    const trimmed = customVibeDraft.trim();
    if (trimmed) {
      setCustomVibe(trimmed);
      setCustomVibeActive(true);
    }
    setEditingCustomVibe(false);
    setErrors(prev => ({ ...prev, vibes: undefined }));
  };

  const handleCustomVibeClear = () => {
    setCustomVibe('');
    setCustomVibeActive(false);
    setEditingCustomVibe(false);
    setCustomVibeDraft('');
  };

  const handleSubmit = (includeClosedPlaces: boolean = false) => {
    const newErrors: { location?: string; vibes?: string } = {};

    if (!location.trim()) {
      newErrors.location = t.input.pleaseEnterLocation;
    }

    const hasActiveCustomVibe = customVibeActive && customVibe.trim();
    if (vibes.length === 0 && !hasActiveCustomVibe) {
      newErrors.vibes = t.input.pleaseSelectVibe;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(
      { location, timeAvailable, transport, vibes, customVibe: hasActiveCustomVibe ? customVibe.trim() : undefined, coords },
      includeClosedPlaces
    );
  };

  const timeOptions = [
    { value: '1-2-hrs', label: t.time['1-2-hrs'] },
    { value: 'half-day', label: t.time['half-day'] },
    { value: 'full-day', label: t.time['full-day'] },
    { value: '2-3-days', label: t.time['2-3-days'] }
  ];

  const transportOptions = [
    { value: 'walking', emoji: '🚶', label: t.transport.walking },
    { value: 'transit', emoji: '🚇', label: t.transport.transit },
    { value: 'taxi', emoji: '🚕', label: t.transport.taxi }
  ];

  const vibeOptions = [
    { value: 'hungry', emoji: VIBE_EMOJIS.hungry, label: t.vibes.hungry },
    { value: 'adventurous', emoji: VIBE_EMOJIS.adventurous, label: t.vibes.adventurous },
    { value: 'must-sees', emoji: VIBE_EMOJIS['must-sees'], label: t.vibes['must-sees'] },
    { value: 'popular', emoji: VIBE_EMOJIS.popular, label: t.vibes.popular },
    { value: 'hidden-gems', emoji: VIBE_EMOJIS['hidden-gems'], label: t.vibes['hidden-gems'] }
  ];

  const getFilteredText = () => {
    const count = filteredClosedCount;
    const s = count !== 1 ? 's' : '';
    return t.input.placesFiltered.replace('{count}', String(count)).replace('{s}', s);
  };

  const shareUrl = referralCode ? `https://tripboom.app?ref=${referralCode}` : 'https://tripboom.app';
  const shareMessage = (t.share?.message?.trim?.() || 'This app planned my whole day out in 10 seconds \u2014 restaurants, bars, things to do. Try it \uD83D\uDC47');
  const shareBody = `${shareMessage}\n\n${shareUrl}`;

  const handleShare = async () => {
    const shareData = {
      title: 'TripBoom',
      text: shareBody,
    };

    if (navigator.share && navigator.canShare?.(shareData)) {
      try {
        await navigator.share(shareData);
      } catch { /* user cancelled share sheet */ }
    } else {
      setShowShareModal(true);
    }
  };

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(shareBody);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareToTwitter = () => {
    const text = encodeURIComponent(`${shareMessage} ${shareUrl}`);
    const url = `https://twitter.com/intent/tweet?text=${text}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleShareToFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleShareToLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleShareViaEmail = () => {
    const subject = encodeURIComponent(t.share.title);
    const body = encodeURIComponent(shareBody);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <>
      {showShareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowShareModal(false)}
            role="presentation"
          />
          <div className="relative w-full max-w-sm rounded-2xl bg-[var(--bg-primary)] border border-[var(--border)] shadow-2xl animate-fade-in p-5" role="dialog" aria-labelledby="share-modal-title">
            <div className="flex items-center justify-between mb-4">
              <h3 id="share-modal-title" className="text-lg font-semibold tracking-tight text-[var(--text-primary)]">{t.share.title}</h3>
              <button
                onClick={() => setShowShareModal(false)}
                aria-label="Close share dialog"
                className="p-1.5 rounded-lg hover:bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleCopyLink}
                aria-label={copied ? 'Link copied' : 'Copy share link'}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)] hover:border-[var(--accent-teal)] text-[var(--text-primary)] transition-all"
              >
                {copied ? (
                  <Check className="w-5 h-5 text-[var(--status-green)]" />
                ) : (
                  <Copy className="w-5 h-5 text-[var(--text-secondary)]" />
                )}
                <span className="text-sm font-medium">{copied ? t.share.copied : t.share.copyLink}</span>
              </button>

              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={handleShareToTwitter}
                  aria-label="Share on X (Twitter)"
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)] hover:border-[var(--text-secondary)] text-[var(--text-primary)] transition-all"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                  <span className="text-sm font-medium">{t.share.twitter}</span>
                </button>

                <button
                  onClick={handleShareToFacebook}
                  aria-label="Share on Facebook"
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)] hover:border-[#1877F2] text-[var(--text-primary)] transition-all"
                >
                  <svg className="w-4 h-4 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  <span className="text-sm font-medium">{t.share.facebook}</span>
                </button>

                <button
                  onClick={handleShareToLinkedIn}
                  aria-label="Share on LinkedIn"
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)] hover:border-[#0A66C2] text-[var(--text-primary)] transition-all"
                >
                  <svg className="w-4 h-4 text-[#0A66C2]" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  <span className="text-sm font-medium">{t.share.linkedin}</span>
                </button>

                <button
                  onClick={handleShareViaEmail}
                  aria-label="Share via email"
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)] hover:border-[var(--accent-gold)] text-[var(--text-primary)] transition-all"
                >
                  <svg className="w-4 h-4 text-[var(--accent-gold)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2"/>
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                  </svg>
                  <span className="text-sm font-medium">{t.share.email}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4 pb-20 overflow-hidden">
        <div className="w-full max-w-md space-y-4 animate-fade-in relative">
          <div className="flex items-center justify-between">
            <LanguageSelector />
            <div className="text-center flex-1">
              <p className="text-2xl font-semibold tracking-tight leading-tight text-[var(--text-primary)]">
                Trip<span style={{ background: 'linear-gradient(to right, #f59e0b, #2dd4bf)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Boom</span>
              </p>
              <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                {t.input.tagline}
              </p>
            </div>
            <button
              onClick={handleShare}
              className="p-2 rounded-full bg-[var(--bg-card)] border border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--text-secondary)] transition-all"
              aria-label={t.share.title}
            >
              <Share2 className="w-4 h-4" />
            </button>
          </div>

        {showBanner && (
          <div className="flex items-center gap-3 p-3 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)] animate-fade-in">
            <Smartphone className="w-5 h-5 text-[var(--accent-teal)] shrink-0" />
            <div className="flex-1 min-w-0">
              {platform === 'ios' ? (
                <p className="text-xs text-[var(--text-secondary)]">
                  Tap <Upload className="w-3 h-3 inline -mt-0.5" /> then <strong className="text-[var(--text-primary)]">{t.installBanner.addToHomeScreen}</strong>
                </p>
              ) : platform === 'mac-safari' ? (
                <p className="text-xs text-[var(--text-secondary)]">
                  Safari menu: <strong className="text-[var(--text-primary)]">File &rarr; {t.installBanner.fileToDock}</strong>
                </p>
              ) : platform === 'android-other' ? (
                <p className="text-xs text-[var(--text-secondary)]">
                  Browser menu &rarr; <strong className="text-[var(--text-primary)]">{t.installBanner.addToHomeScreen}</strong>
                </p>
              ) : (
                <p className="text-xs text-[var(--text-secondary)]">
                  Install TripBoom for the <strong className="text-[var(--text-primary)]">{t.installBanner.bestExperience}</strong>
                </p>
              )}
            </div>
            {platform === 'chromium' && isInstallable && (
              <button
                onClick={async () => { await promptInstall(); }}
                aria-label="Install TripBoom app"
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[var(--accent-teal)] text-white hover:bg-[var(--accent-teal)]/90 transition-colors shrink-0"
              >
                {t.installBanner.install}
              </button>
            )}
            <button
              onClick={dismissBanner}
              className="p-1 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors shrink-0"
              aria-label={t.installBanner.dismiss}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        <div className="glass-morphism rounded-2xl p-5 space-y-4">
          <div className="space-y-2">
            <label className={`block text-sm font-medium text-center transition-all ${
              !location && !useCurrentLocation
                ? 'text-[var(--accent-teal)] label-throb'
                : 'text-[var(--text-primary)]'
            }`}>
              {t.input.whereAreYou}
            </label>
            <input
              ref={locationInputRef}
              type="text"
              value={location}
              onChange={(e) => {
                setLocation(e.target.value);
                setCoords(undefined);
                setUseCurrentLocation(false);
                setErrors(prev => ({ ...prev, location: undefined }));
              }}
              onFocus={() => {
                setLocation('');
                setCoords(undefined);
                if (useCurrentLocation) {
                  setUseCurrentLocation(false);
                }
              }}
              placeholder={t.input.locationPlaceholder}
              aria-label={t.input.whereAreYou}
              className="w-full px-4 py-3 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border)] text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none focus:border-[var(--accent-teal)] transition-colors text-center"
            />
            {errors.location && (
              <p className="text-xs text-[var(--status-red)] text-center">{errors.location}</p>
            )}
            {/* H-102: surface geolocation errors inline with a retry + manual-entry affordance */}
            {geoError && !geoLoading && (
              <div className="flex flex-col items-center gap-1" role="alert">
                <p className="text-xs text-[var(--status-red)] text-center">{geoError}</p>
                <div className="flex items-center gap-3 text-xs">
                  <button
                    type="button"
                    onClick={handleAutoLocation}
                    className="underline text-[var(--accent-teal)] hover:opacity-80"
                  >
                    {t.input.locationErrorRetry || 'Try again'}
                  </button>
                  <span className="text-[var(--text-secondary)]">·</span>
                  <button
                    type="button"
                    onClick={() => {
                      setUseCurrentLocation(false);
                      locationInputRef.current?.focus();
                    }}
                    className="underline text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                  >
                    {t.input.locationErrorEnterManually || 'Or enter it manually'}
                  </button>
                </div>
              </div>
            )}
            <div className="flex justify-center">
              <button
                onClick={toggleUseCurrentLocation}
                disabled={geoLoading}
                aria-label={t.input.useMyCurrentLocation}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition-all disabled:opacity-50 ${
                  useCurrentLocation
                    ? 'bg-[var(--accent-gold)] text-[var(--bg-primary)]'
                    : 'bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text-primary)]'
                }`}
              >
                {geoLoading ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <MapPin className="w-3 h-3" />
                )}
                {t.input.useMyCurrentLocation}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-[var(--text-primary)] text-center">
              {t.input.howLong}
            </label>
            <div className="grid grid-cols-4 gap-2">
              {timeOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setTimeAvailable(option.value)}
                  aria-pressed={timeAvailable === option.value}
                  aria-label={option.label}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all text-center ${
                    timeAvailable === option.value
                      ? 'bg-[var(--accent-gold)] text-[var(--bg-primary)]'
                      : 'bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text-primary)]'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-[var(--text-primary)] text-center">
              {t.input.howGettingAround}
            </label>
            <div className="grid grid-cols-3 gap-2">
              {transportOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setTransport(option.value)}
                  aria-pressed={transport === option.value}
                  aria-label={option.label}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex flex-col items-center gap-0.5 ${
                    transport === option.value
                      ? 'bg-[var(--accent-gold)] text-[var(--bg-primary)]'
                      : 'bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text-primary)]'
                  }`}
                >
                  <span className="text-lg">{option.emoji}</span>
                  <span className="text-xs">{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className={`text-sm font-medium transition-all ${
                vibes.length === 0 && !(customVibeActive && customVibe)
                  ? 'text-[var(--accent-teal)] label-throb'
                  : 'text-[var(--text-primary)]'
              }`}>
                {t.input.imFeeling}
              </label>
              {onToggleSavedInterests && hasSavedInterests && (
                <button
                  onClick={() => isPremium ? onToggleSavedInterests(!savedInterestsActive) : undefined}
                  className={`flex items-center gap-1.5 px-1 py-1 text-[11px] font-medium transition-all ${
                    !isPremium
                      ? 'opacity-50 cursor-not-allowed text-[var(--text-secondary)]'
                      : savedInterestsActive
                      ? 'text-[var(--accent-teal)]'
                      : 'text-[var(--text-secondary)]'
                  }`}
                  disabled={!isPremium}
                  aria-pressed={savedInterestsActive}
                  aria-label={!isPremium ? 'Use saved interests (Pro required)' : savedInterestsActive ? 'Saved interests active' : 'Use your saved interests'}
                >
                  <Bookmark className="w-3 h-3" />
                  Use Interests
                  {!isPremium && (
                    <span className="text-[9px] text-[var(--accent-gold)] font-semibold">PRO</span>
                  )}
                  <div className={`w-7 h-4 rounded-full transition-all flex items-center ${
                    savedInterestsActive && isPremium ? 'bg-[var(--accent-teal)] justify-end' : 'bg-[var(--border)] justify-start'
                  }`}>
                    <div className="w-3 h-3 rounded-full bg-white mx-0.5 shadow-sm" />
                  </div>
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {vibeOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleVibeToggle(option.value)}
                  aria-pressed={vibes.includes(option.value)}
                  aria-label={option.label}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                    vibes.includes(option.value)
                      ? 'border-[var(--accent-gold)] border-2 bg-[var(--accent-gold)]/10 text-[var(--text-primary)]'
                      : 'border border-[var(--border)] bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:border-[var(--accent-gold)]/50 hover:text-[var(--text-primary)]'
                  }`}
                >
                  <span className="text-base">{option.emoji}</span>
                  <span className="text-xs">{option.label}</span>
                </button>
              ))}

              <div className="relative flex items-center">
                {editingCustomVibe ? (
                  <>
                    <Wand2 className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--accent-teal)] z-10 pointer-events-none" />
                    <input
                      ref={customVibeInputRef}
                      type="text"
                      value={customVibeDraft}
                      onChange={(e) => setCustomVibeDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleCustomVibeConfirm();
                        if (e.key === 'Escape') setEditingCustomVibe(false);
                      }}
                      onBlur={handleCustomVibeConfirm}
                      placeholder="e.g. Sushi, Rooftop..."
                      maxLength={30}
                      className="w-full pl-7 pr-8 py-2 rounded-lg text-xs bg-[var(--bg-elevated)] border-2 border-[var(--accent-teal)] text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none"
                    />
                    <button
                      onMouseDown={(e) => { e.preventDefault(); handleCustomVibeConfirm(); }}
                      disabled={!customVibeDraft.trim()}
                      aria-label="Confirm custom vibe"
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 px-1.5 py-0.5 rounded text-xs font-semibold text-[var(--accent-teal)] disabled:opacity-30 transition-all"
                    >
                      OK
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleCustomVibeEdit}
                      aria-label={customVibe ? `Edit custom vibe: ${customVibe}` : 'Add custom vibe'}
                      aria-pressed={customVibeActive && !!customVibe}
                      style={{ paddingRight: customVibe ? '2rem' : undefined }}
                      className={`flex-1 pl-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 min-w-0 ${
                        customVibeActive && customVibe
                          ? 'border-[var(--accent-teal)] border-2 bg-[var(--accent-teal)]/10 text-[var(--text-primary)]'
                          : 'border border-dashed border-[var(--border)] bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:border-[var(--accent-teal)]/60 hover:text-[var(--text-primary)]'
                      }`}
                    >
                      <Wand2 className={`w-4 h-4 shrink-0 ${customVibeActive && customVibe ? 'text-[var(--accent-teal)]' : 'text-[var(--text-secondary)]'}`} />
                      <span className="text-xs truncate">
                        {customVibe || t.input.interestedIn}
                      </span>
                    </button>
                    {customVibe ? (
                      <div className="absolute right-1.5 flex items-center gap-0.5">
                        <Pencil className="w-3 h-3 text-[var(--text-secondary)] pointer-events-none" />
                        <button
                          onClick={(e) => { e.stopPropagation(); handleCustomVibeClear(); }}
                          aria-label="Clear custom vibe"
                          className="p-0.5 rounded text-[var(--text-secondary)] hover:text-[var(--status-red)] transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <Pencil className="absolute right-1.5 w-3 h-3 text-[var(--text-secondary)] pointer-events-none" />
                    )}
                  </>
                )}
              </div>
            </div>
            {errors.vibes && (
              <p className="text-xs text-[var(--status-red)]">{errors.vibes}</p>
            )}
          </div>

          {filteredClosedCount > 0 && !errorMessage && (
            <div className="p-4 rounded-lg bg-[var(--status-amber-bg)] border border-[var(--status-amber)]/40 text-center">
              <p className="text-sm text-[var(--status-amber)] mb-2">
                {getFilteredText()}
              </p>
              <button
                onClick={() => handleSubmit(true)}
                className="text-xs px-3 py-1.5 rounded-lg bg-[var(--status-amber-bg)] text-[var(--status-amber)] hover:bg-[var(--status-amber)]/20 transition-colors"
              >
                {t.input.includeClosedPlaces}
              </button>
            </div>
          )}

          {errorMessage && errorMessage.includes('CREDITS_EXHAUSTED') ? (
            <div className="p-4 rounded-xl bg-[var(--accent-gold)]/10 border border-[var(--accent-gold)]/30 text-center space-y-3">
              <p className="text-sm text-[var(--text-primary)] leading-relaxed">
                {t.errors.creditsExhaustedBody}
              </p>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => {
                    setCustomVibe('');
                    setCustomVibeActive(false);
                    onDismissError?.();
                  }}
                  aria-label="Dismiss credits exhausted message"
                  className="px-4 py-2 rounded-lg text-xs font-medium text-[var(--text-secondary)] bg-[var(--bg-elevated)] border border-[var(--border)] hover:bg-[var(--bg-primary)] transition-colors"
                >
                  {t.errors.dismiss}
                </button>
                {onOpenPro && (
                  <button
                    onClick={() => {
                      onDismissError?.();
                      onOpenPro();
                    }}
                    aria-label="Upgrade to Pro for unlimited searches"
                    className="px-4 py-2 rounded-lg text-xs font-semibold text-black bg-[var(--accent-gold)] hover:brightness-110 transition-all"
                  >
                    {t.errors.goPro}
                  </button>
                )}
              </div>
            </div>
          ) : errorMessage ? (
            <div className="relative p-4 rounded-lg bg-[var(--status-red-bg)] border border-[var(--status-red)]/40 text-center">
              {onDismissError && (
                <button
                  onClick={() => {
                    setCustomVibe('');
                    setCustomVibeActive(false);
                    onDismissError();
                  }}
                  aria-label="Dismiss error"
                  className="absolute top-2 right-2 p-1 rounded-lg text-[var(--status-red)]/60 hover:text-[var(--status-red)] transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              <p className="text-sm text-[var(--status-red)]">{errorMessage}</p>
              {filteredClosedCount > 0 && (
                <button
                  onClick={() => handleSubmit(true)}
                  className="mt-2 text-xs px-3 py-1.5 rounded-lg bg-[var(--status-amber-bg)] text-[var(--status-amber)] hover:bg-[var(--status-amber)]/20 transition-colors"
                >
                  {t.input.includeClosedPlaces}
                </button>
              )}
            </div>
          ) : null}

        </div>

          <button
            onClick={() => handleSubmit(false)}
            className="w-full py-3 rounded-lg font-medium text-white transition-all"
            style={{
              background: 'linear-gradient(135deg, var(--accent-gold) 0%, var(--accent-teal) 100%)'
            }}
          >
            {t.input.findMyAdventure}
          </button>

        </div>
      </div>
    </>
  );
}
